/**
 * GLDN Gift Note — per-item gift option for Horizon cart
 *
 * Injects a "Gift, box separately" checkbox + To/From/Note form
 * inside each cart item row (as a grid-spanning cell). Saves gift
 * details as line item properties via /cart/change.js. Re-injects
 * after Horizon's morphSection updates.
 */
(function () {
  'use strict';

  const DELIMITER = ' -|- ';
  const MAX_NOTE_LENGTH = 60;

  /* #599 — Redo Checkout+ hides the theme's checkout button and draws its own
   * inside a CLOSED shadow root on <redo-shopify-toggle>, then sends the
   * shopper to /checkout itself, so the guard below never saw the click: gift
   * details went unchecked and, if never saved, were lost. Keep a private
   * reference to that root (its mode is unchanged, still closed to everyone
   * else) so interceptRedoCheckout can tell which of Redo's controls was
   * tapped and click it again once the gift details are saved. This runs on
   * the deferred script, ahead of Redo's async widget.
   *
   * The state lives on one page-level object: this file is included twice on
   * /cart (layout + the cart block), and a second copy with its own flags
   * would hold the first copy's resume click. */
  var redo = window.__gldnGiftRedo || (window.__gldnGiftRedo = {
    roots: new WeakMap(), resuming: false, pass: null, listening: false, swallowClickUntil: 0, inFlight: false
  });
  var redoRoots = redo.roots;
  (function captureRedoRoots() {
    var original = Element.prototype.attachShadow;
    if (typeof original !== 'function' || original._gldnRedoWrapped) return;
    var wrapped = function (init) {
      var root = original.call(this, init);
      if (this.localName === 'redo-shopify-toggle') redoRoots.set(this, root);
      return root;
    };
    wrapped._gldnRedoWrapped = true;
    Element.prototype.attachShadow = wrapped;
  })();

  /* #593 — gift notes are handwritten, so emoji are stripped from To / From /
   * Gift Note as the customer types, and a notice explains why.
   *
   * What counts as an emoji: anything that renders as one. That is an
   * Emoji_Presentation character (😀 🙂 ⌚), any pictograph followed by U+FE0F
   * (❤️ ©️ ♥️), a keycap (1️⃣), a flag, or any pictograph in the astral emoji
   * planes (future emoji the browser's Unicode tables don't know yet are still
   * caught there) — each with its skin-tone modifiers, tag characters and ZWJ
   * continuations, so 👩🏽‍💻 goes as one unit. A text symbol WITHOUT U+FE0F
   * (♥ ★ © ® ™) is something a pen can draw and is kept, as are all letters,
   * digits, whitespace and punctuation.
   *
   * A U+200D not touching an emoji is deliberately left alone: between letters
   * it is a joiner in Indic and Arabic script, and stripping it would alter
   * real names. */
  const EMOJI_WARNING = 'We handwrite all gift notes, so emojis have been removed from your message.';
  const EMOJI_CONT = '(?:\\p{Emoji_Modifier}|\\uFE0F|[\\u{E0020}-\\u{E007F}])*';
  const EMOJI_BASE = '(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic}\\uFE0F|(?=\\p{Extended_Pictographic})[\\u{1F000}-\\u{1FFFF}])';
  // After a ZWJ any pictograph joins the sequence, U+FE0F or not (👁‍🗨, 🏳‍🌈).
  const EMOJI_ZWJ_PART = '\\u200D(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic})' + EMOJI_CONT;
  const EMOJI_RE = new RegExp(
    '[0-9#*]\\uFE0F?\\u20E3' +                                    // keycap 1️⃣
    // emoji + modifiers + ZWJ chain. A ZWJ directly before or after an emoji
    // goes with it: a sequence typed one code point at a time (👩, ZWJ, 💻)
    // otherwise strands the joiner as an invisible character in the note.
    '|\\u200D?' + EMOJI_BASE + EMOJI_CONT + '(?:' + EMOJI_ZWJ_PART + ')*\\u200D?' +
    '|\\p{Regional_Indicator}|\\p{Emoji_Modifier}|\\uFE0F|\\u20E3', // orphans left by partial edits
    'gu'
  );

  /** Remove emoji from a string. Returns { value, stripped }. Never trims or
   *  collapses whitespace, so 'Hi😀 there🎉' becomes 'Hi there'. */
  function stripEmoji(str) {
    var s = String(str == null ? '' : str);
    var value = s.replace(EMOJI_RE, '');
    return { value: value, stripped: value !== s };
  }

  /** A field's value as it may be saved: emoji removed, trimmed. */
  function cleanValue(field) {
    return field ? stripEmoji(field.value).value.trim() : '';
  }

  /** Show the emoji notice inside the field's own .gldn-gift-field wrapper.
   *  Idempotent. Kept apart from .gldn-gift-field-error so the required-field
   *  error and this notice can show together and clear independently. */
  function showEmojiWarning(field) {
    var wrap = field && field.closest('.gldn-gift-field');
    if (!wrap) return;
    var note = wrap.querySelector('.gldn-gift-emoji-warning');
    if (!note) {
      note = document.createElement('div');
      note.className = 'gldn-gift-emoji-warning';
      note.id = field.id + '-emoji';
      note.setAttribute('role', 'status');
      note.setAttribute('aria-live', 'polite');
      note.textContent = EMOJI_WARNING;
      wrap.appendChild(note);
    }
    field.setAttribute('aria-describedby', note.id);
  }

  function clearEmojiWarning(field) {
    var wrap = field && field.closest('.gldn-gift-field');
    if (!wrap) return;
    var note = wrap.querySelector('.gldn-gift-emoji-warning');
    if (note) note.remove();
    if (field.getAttribute('aria-describedby') === field.id + '-emoji') {
      field.removeAttribute('aria-describedby');
    }
  }

  /** Strip emoji from a field on every input — typing, paste, drop, the OS
   *  emoji picker. Must be bound BEFORE the textarea's maxlength/counter
   *  listener so those run on the cleaned text. IME composition is left alone
   *  until it commits, or the half-composed text would be rewritten mid-word. */
  function bindEmojiGuard(field) {
    if (!field || field._gldnEmojiWired) return;
    field._gldnEmojiWired = true;
    /* Set only by a compositionend pass that stripped something. Some browsers
       (Safari) fire one more, unchanged input event straight after
       compositionend; that is not "an input that strips nothing" and must not
       clear the notice the composition pass just showed. */
    var strippedOnCommit = null;
    function guard(e) {
      if (e && e.isComposing) return false;
      var result = stripEmoji(field.value);
      if (result.stripped) {
        var caret = field.selectionStart;
        var before = caret == null ? null : stripEmoji(field.value.slice(0, caret)).value.length;
        field.value = result.value;
        if (before != null && document.activeElement === field) {
          field.setSelectionRange(before, before);
        }
        showEmojiWarning(field);
        return true;
      }
      var echo = strippedOnCommit !== null && field.value === strippedOnCommit;
      strippedOnCommit = null;
      if (!echo) clearEmojiWarning(field);
      return false;
    }
    field.addEventListener('input', guard);
    /* Where the input events fired mid-composition the committed text is only
       final here (Chrome). Strip it, and keep the note's counter in step since
       its listener already ran on the uncleaned text. */
    field.addEventListener('compositionend', function () {
      if (!guard(null)) return;
      strippedOnCommit = field.value;
      var wrap = field.closest('.gldn-gift-field');
      var count = wrap && wrap.querySelector('.gldn-gift-count');
      if (count) count.textContent = field.value.length;
    });
  }

  /* Bounded retry for /cart/change.js. Under rapid cart mutation Shopify
   * throttles with 429/503 (often returning an HTML throttle page, not
   * JSON). The original save surfaced that as a one-shot console.error and
   * silently dropped the gift note — the field still showed the typed text,
   * so the customer never knew it didn't persist. Retry with backoff, then
   * surface a visible error. */
  const CART_CHANGE_MAX_RETRIES = 3;
  const CART_CHANGE_BACKOFF_MS = 400; // 400ms → 800ms → 1600ms

  /* #418 — the cart drawer's Subtotal/Checkout summary is position:sticky at
   * the bottom of the drawer's scroll container, so it floats over whatever is
   * mid-scroll beneath it. Opening the gift editor mid-list therefore dropped
   * FROM / Gift Note / Save under the summary, where they were neither visible
   * nor clickable (elementFromPoint at the Save button returned .cart-totals).
   *
   * The fix is two halves. header-actions.liquid sets scroll-padding-block-end
   * on the scroll container, so every scroll-into-view — ours below, and the
   * browser's own focus scroll on iOS — lands clear of the summary. Here we
   * keep that padding equal to the summary's real height and reveal the form
   * when it opens. */

  /** Publish the drawer summary's measured height as --gldn-cart-summary-h on
   *  its scroll container. No-op outside the drawer (the cart page has no
   *  sticky summary). */
  function syncCartSummaryHeight() {
    var summary = document.querySelector('.cart-drawer__summary');
    var scroller = document.querySelector('.cart-drawer__content');
    if (!summary || !scroller) return;
    var h = summary.getBoundingClientRect().height;
    if (h > 0) scroller.style.setProperty('--gldn-cart-summary-h', Math.ceil(h) + 'px');
  }

  /** The element that actually scrolls the form: the nearest ancestor with a
   *  vertical scrollbar in use. */
  function scrollParent(el) {
    for (var p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
      var oy = getComputedStyle(p).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight + 1) return p;
    }
    return null;
  }

  /** Scroll an opened gift form fully into view, clear of the sticky summary.
   *  Deferred a frame so the form's `open` class has been laid out first. */
  function revealGiftForm(form) {
    if (!form || typeof form.scrollIntoView !== 'function') return;
    requestAnimationFrame(function () {
      syncCartSummaryHeight();
      /* #599 — in the drawer, scroll the real scroller by an explicit amount,
       * instantly. The item list (.cart-drawer__items) is its own scroller now,
       * not .cart-drawer__content, so the scroll-padding above never applies
       * to it; and WebKit's smooth scrolling (scrollIntoView or scrollBy)
       * stopped short there, leaving Save under the summary on a 320x568
       * phone. */
      var scroller = form.closest('.cart-drawer__content') ? scrollParent(form) : null;
      if (!scroller) {
        /* 'nearest' does nothing when the form is already fully visible — so
         * this never yanks the cart page, which has no sticky summary. */
        form.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        return;
      }
      var box = scroller.getBoundingClientRect();
      var bottom = box.bottom;
      var summary = document.querySelector('.cart-drawer__summary');
      if (summary && summary.offsetParent !== null) {
        var summaryTop = summary.getBoundingClientRect().top;
        if (summaryTop > box.top && summaryTop < bottom) bottom = summaryTop;
      }
      var margin = parseFloat(getComputedStyle(form).scrollMarginBlockEnd) || 0;
      var r = form.getBoundingClientRect();
      var delta = Math.max(0, r.bottom + margin - bottom);
      // Taller than the space: show its top rather than its bottom.
      if (r.top - delta < box.top) delta = r.top - box.top;
      if (Math.abs(delta) >= 1) scroller.scrollBy({ top: delta });
    });
  }

  /** POST /cart/change.js, retrying transient throttle responses. Resolves
   *  with the parsed cart JSON, or rejects after exhausting retries. */
  function postCartChange(body, attempt) {
    attempt = attempt || 0;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (res.ok) return res.json();
      var transient = res.status === 429 || res.status === 502 ||
                      res.status === 503 || res.status === 504;
      if (transient && attempt < CART_CHANGE_MAX_RETRIES) {
        var delay = CART_CHANGE_BACKOFF_MS * Math.pow(2, attempt);
        return new Promise(function (resolve) { setTimeout(resolve, delay); })
          .then(function () { return postCartChange(body, attempt + 1); });
      }
      throw new Error('Cart update failed: ' + res.status);
    });
  }

  /** Surface a save failure in the gift cell and keep the editor open so
   *  the typed note isn't visually lost. */
  function showSaveError(cell) {
    if (!cell) return;
    var form = cell.querySelector('.gldn-gift-form');
    if (form) { form.classList.add('open'); form.classList.remove('saved'); }
    cell.querySelectorAll('.gldn-gift-to, .gldn-gift-from').forEach(function (input) {
      input.readOnly = false;
    });
    var holder = cell.querySelector('.gldn-gift-fields') || cell;
    var err = cell.querySelector('.gldn-gift-save-error');
    if (!err) {
      err = document.createElement('div');
      err.className = 'gldn-gift-save-error';
      err.setAttribute('role', 'alert');
      holder.appendChild(err);
    }
    err.textContent = "Couldn't save your gift note — please try again.";
  }

  function clearSaveError(cell) {
    if (!cell) return;
    var err = cell.querySelector('.gldn-gift-save-error');
    if (err) err.remove();
  }

  /** Parse the joined "Gift To: X -|- Gift From: Y -|- Gift Note: Z" string */
  function parseGiftNote(raw) {
    const result = { to: '', from: '', note: '' };
    if (!raw) return result;
    const parts = raw.split(DELIMITER);
    for (const part of parts) {
      if (part.startsWith('Gift To:')) result.to = part.replace('Gift To:', '').trim();
      else if (part.startsWith('Gift From:')) result.from = part.replace('Gift From:', '').trim();
      else if (part.startsWith('Gift Note:')) result.note = part.replace('Gift Note:', '').trim();
    }
    return result;
  }

  /** Build the joined string from fields */
  function joinGiftNote(to, from, note) {
    const parts = [];
    if (to) parts.push('Gift To: ' + to);
    if (from) parts.push('Gift From: ' + from);
    if (note) parts.push('Gift Note: ' + note);
    return parts.join(DELIMITER);
  }

  /** Read existing gift properties from the DOM property list */
  function readGiftProps(row) {
    const props = row.querySelectorAll('.cart-items__property');
    let isGift = false;
    let giftNoteRaw = '';

    props.forEach(function (prop) {
      const dt = prop.querySelector('dt');
      const dd = prop.querySelector('dd');
      if (!dt || !dd) return;
      const key = dt.textContent.replace(':', '').trim();
      const val = dd.textContent.trim();

      if (key === 'Gift' && val === 'Yes') {
        isGift = true;
        dt.setAttribute('data-gldn-gift-prop', '');
      } else if (key === 'Gift Note') {
        giftNoteRaw = val;
        dt.setAttribute('data-gldn-gift-prop', '');
      }
    });

    return { isGift: isGift, parsed: parseGiftNote(giftNoteRaw) };
  }

  /** Create the gift note UI inside a single cart row */
  function createGiftUI(row) {
    /* Skip add-on / tag rows — they roll up under the parent builder
     * item and shouldn't carry their own gift-box checkbox or note. */
    if (row.classList.contains('cart-items__table-row--addon')) return;

    /* Skip gift card products — the gift card IS the gift, so a
     * separate "Add gift box & message" option doesn't apply. Flagged
     * server-side via data-gldn-gift-card on the row when
     * item.product.gift_card? is true. */
    if (row.getAttribute('data-gldn-gift-card') === 'true') return;

    /* Skip free / app-managed items — the free ring sizer and BOGOS freebies
     * ($0 lines) aren't things you gift-wrap, and doing so can race the
     * free-gift apps (gldn45 #334 QA H3). data-gldn-free = item.final_price==0. */
    if (row.getAttribute('data-gldn-free') === 'true' ||
        row.getAttribute('data-gldn-ring-sizer') === 'true') return;

    var key = row.getAttribute('data-key');
    if (!key) return;

    /* Skip if a gift cell for THIS line item key already exists anywhere
     * in the table (it may have been moved to the last addon row of the
     * builder set, so the row.querySelector below would miss it). */
    var existingCell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + key + '"]');
    if (existingCell) return;

    var data = readGiftProps(row);
    var uid = 'gldn-gift-' + key.replace(/[^a-zA-Z0-9]/g, '-');

    /* #599 — one label for every cart. The old 1-item "Add Gift Message"
     * branch never fired: its row count also matched the hidden <thead> row
     * of every cart table, the drawer's included. */
    var labelText = 'Add free gift box &amp; note';

    var cell = document.createElement('td');
    cell.className = 'gldn-gift-cell';
    cell.setAttribute('data-gldn-for-key', key);

    cell.innerHTML =
      '<div class="gldn-gift-check">' +
        '<label>' +
          '<input type="checkbox" class="gldn-gift-checkbox" id="' + uid + '"' +
            (data.isGift ? ' checked' : '') + '>' +
          '<span class="gldn-gift-label">' +
            '<span class="gldn-gift-label-main">' + labelText + '</span>' +
          '</span>' +
        '</label>' +
        '<button type="button" class="gldn-gift-edit"' +
          (data.isGift ? '' : ' style="display:none"') + '>Edit Gift Details</button>' +
      '</div>' +
      '<div class="gldn-gift-form' + (data.isGift ? ' open saved' : '') + '">' +
        /* #599 — the saved view. Filled from the fields by renderGiftSummary. */
        '<div class="gldn-gift-summary">' +
          '<div class="gldn-gift-summary-names">' +
            '<span class="gldn-gift-summary-to">To: <span class="gldn-gift-summary-value"></span></span>' +
            '<span class="gldn-gift-summary-from">From: <span class="gldn-gift-summary-value"></span></span>' +
          '</div>' +
          '<p class="gldn-gift-summary-note"></p>' +
        '</div>' +
        /* #599 — placeholders in the boxes instead of labels above them; the
           aria-label keeps each field's accessible name. */
        '<div class="gldn-gift-fields">' +
          '<div class="gldn-gift-field">' +
            '<input type="text" id="' + uid + '-to" class="gldn-gift-to" placeholder="To" aria-label="To" value="' + escapeAttr(data.parsed.to) + '"' + (data.isGift ? ' readonly' : '') + '>' +
          '</div>' +
          '<div class="gldn-gift-field">' +
            '<input type="text" id="' + uid + '-from" class="gldn-gift-from" placeholder="From" aria-label="From" value="' + escapeAttr(data.parsed.from) + '"' + (data.isGift ? ' readonly' : '') + '>' +
          '</div>' +
          '<div class="gldn-gift-field gldn-gift-note-wrap">' +
            /* The counter sits inside the textarea's corner, so it is anchored
               to this box rather than the field wrapper: the #593 emoji notice
               appended below would otherwise drag it down onto the notice. */
            '<div class="gldn-gift-note-box">' +
              '<textarea id="' + uid + '-note" class="gldn-gift-textarea" placeholder="Gift message (optional)" aria-label="Gift message (optional)" maxlength="' + MAX_NOTE_LENGTH + '">' +
                escapeHtml(data.parsed.note) +
              '</textarea>' +
              '<span class="gldn-gift-counter"><span class="gldn-gift-count">' +
                data.parsed.note.length + '</span>/' + MAX_NOTE_LENGTH +
              '</span>' +
            '</div>' +
          '</div>' +
          '<button type="button" class="gldn-gift-save">Save Gift Details</button>' +
        '</div>' +
      '</div>';

    /* For builder sets with add-ons, the gift checkbox should sit at the
     * bottom of the entire set (after the last add-on row), matching live.
     * Walk forward from the main row through siblings of the same builder
     * id and prefer the LAST one as the host. */
    var hostRow = row;
    var builderId = row.getAttribute('data-gldn-builder-id');
    if (builderId) {
      var sibling = row.nextElementSibling;
      while (sibling) {
        if (sibling.getAttribute && sibling.getAttribute('data-gldn-builder-id') === builderId) {
          hostRow = sibling;
        } else if (
          sibling.classList &&
          !sibling.classList.contains('cart-items__addons-header-row') &&
          !sibling.classList.contains('gldn-gift-row')
        ) {
          /* Hit a non-builder, non-header row — end of this set. */
          break;
        }
        sibling = sibling.nextElementSibling;
      }
    }

    // Insert BEFORE the Special Request cell if one exists, so the visual
    // grid order (both cells use grid-column: 1/-1) is: row → gift → request.
    var requestCell = hostRow.querySelector('.cart-items__request-cell');
    if (requestCell) {
      hostRow.insertBefore(cell, requestCell);
    } else {
      hostRow.appendChild(cell);
    }

    renderGiftSummary(cell);

    // Bind events
    var checkbox = cell.querySelector('.gldn-gift-checkbox');
    var editBtn = cell.querySelector('.gldn-gift-edit');
    var form = cell.querySelector('.gldn-gift-form');
    var textarea = cell.querySelector('.gldn-gift-textarea');
    var counter = cell.querySelector('.gldn-gift-count');
    var saveBtn = cell.querySelector('.gldn-gift-save');

    /* To/From are read-only in the collapsed "saved" state so an edit can't be
       silently lost (it must go through "Edit Gift Details" → Save). Toggle the
       readonly flag alongside the saved/open state. */
    function setFieldsEditable(editable) {
      var to = cell.querySelector('.gldn-gift-to');
      var from = cell.querySelector('.gldn-gift-from');
      if (to) to.readOnly = !editable;
      if (from) from.readOnly = !editable;
    }

    checkbox.addEventListener('change', function () {
      if (checkbox.checked) {
        /* Newly checked → full editor open (no saved state yet). */
        form.classList.add('open');
        form.classList.remove('saved');
        editBtn.style.display = '';
        setFieldsEditable(true);
        revealGiftForm(form); // #418 — keep the editor clear of the sticky summary
        /* #599 — preventScroll: the focus scroll lands after the reveal's
           frame and cancels it, stranding Save under the summary on a short
           phone. The reveal alone brings the whole editor, To included, into
           view. */
        var toInput = cell.querySelector('.gldn-gift-to');
        if (toInput) toInput.focus({ preventScroll: true });
      } else {
        form.classList.remove('open');
        form.classList.remove('saved');
        editBtn.style.display = 'none';
        saveGiftDetails(cell, row, key);
      }
    });

    editBtn.addEventListener('click', function () {
      /* Edit always expands to full editor (To/From + Note + Save). */
      form.classList.add('open');
      form.classList.remove('saved');
      setFieldsEditable(true);
      revealGiftForm(form); // #418 — keep the editor clear of the sticky summary
    });

    /* #593 — bound before the counter listener below so maxlength and the
       count apply to the emoji-free text. */
    bindEmojiGuard(cell.querySelector('.gldn-gift-to'));
    bindEmojiGuard(cell.querySelector('.gldn-gift-from'));
    bindEmojiGuard(textarea);

    textarea.addEventListener('input', function () {
      if (textarea.value.length > MAX_NOTE_LENGTH) {
        textarea.value = textarea.value.substring(0, MAX_NOTE_LENGTH);
      }
      counter.textContent = textarea.value.length;
    });

    saveBtn.addEventListener('click', function () {
      /* Validate the To field before saving — gift box requires a
         recipient name. Outline the field red and show an inline
         error if empty. From is allowed to be blank at Save time
         (final To+From check runs at Checkout). */
      var toInput = cell.querySelector('.gldn-gift-to');
      if (!cleanValue(toInput)) {
        showFieldError(cell, '.gldn-gift-to', 'Recipient name is required.');
        if (toInput) toInput.focus();
        return;
      }
      clearFieldError(cell, '.gldn-gift-to');
      saveGiftDetails(cell, row, key);
      /* Collapse to "saved" state: the fields give way to the To/From/message
         summary. "Edit Gift Details" re-expands + re-enables. */
      renderGiftSummary(cell);
      form.classList.add('saved');
      setFieldsEditable(false);
    });
  }

  /** #599 — fill the saved view from the cell's own fields: To and From on one
   *  row, the message under them. An empty part is hidden, so a saved note
   *  with no message shows no message line. Values go in as textContent. */
  function renderGiftSummary(cell) {
    if (!cell) return;
    var parts = [
      ['.gldn-gift-summary-to', '.gldn-gift-to'],
      ['.gldn-gift-summary-from', '.gldn-gift-from'],
      ['.gldn-gift-summary-note', '.gldn-gift-textarea']
    ];
    parts.forEach(function (p) {
      var out = cell.querySelector(p[0]);
      if (!out) return;
      var value = cleanValue(cell.querySelector(p[1]));
      var target = out.querySelector('.gldn-gift-summary-value') || out;
      if (target.textContent !== value) target.textContent = value;
      if (out.hidden !== !value) out.hidden = !value;
    });
  }

  /** Show an inline error under a gift form field. Idempotent — re-uses
   *  the same error node if already present. */
  function showFieldError(scope, fieldSelector, message) {
    var field = scope.querySelector(fieldSelector);
    if (!field) return;
    field.classList.add('gldn-gift-field-invalid');
    var wrap = field.closest('.gldn-gift-field') || field.parentElement;
    if (!wrap) return;
    var err = wrap.querySelector('.gldn-gift-field-error');
    if (!err) {
      err = document.createElement('div');
      err.className = 'gldn-gift-field-error';
      wrap.appendChild(err);
    }
    err.textContent = message;
    /* Clear the error the next time the user edits the field. */
    if (!field._gldnErrorClearWired) {
      field.addEventListener('input', function () {
        if (field.value.trim()) clearFieldError(scope, fieldSelector);
      });
      field._gldnErrorClearWired = true;
    }
  }

  function clearFieldError(scope, fieldSelector) {
    var field = scope.querySelector(fieldSelector);
    if (!field) return;
    field.classList.remove('gldn-gift-field-invalid');
    var wrap = field.closest('.gldn-gift-field') || field.parentElement;
    if (!wrap) return;
    var err = wrap.querySelector('.gldn-gift-field-error');
    if (err) err.remove();
  }

  /** Save gift details via /cart/change.js
   *
   * /cart/change.js REPLACES the line item's properties hash — any key not
   * included in the request gets wiped. The old implementation collected
   * "existing" properties from .cart-items__property DOM elements, which
   * doesn't include internal/underscore-prefixed props or anything the
   * cart template filtered out (Builder, Preview, Customization, etc.).
   * That silently destroyed personalizer metadata on every gift save.
   *
   * Fetch the canonical line item from /cart.js so we preserve every
   * property the server knows about. Strip any http:// URL values — those
   * are images/uploads that Shopify re-resolves, and we should send them
   * as the original property value, not a mutated DOM text.
   */
  function saveGiftDetails(cell, row, itemKey) {
    // cell is passed directly by the caller (closure-captured in
    // createGiftUI). Do NOT re-query it from `row` — for a builder set
    // with add-ons, the gift cell is appended to the LAST add-on row
    // (hostRow), not the main `row`. row.querySelector('.gldn-gift-cell')
    // returns null there, which made Save Gift Details a silent no-op for
    // every personalized item that has an add-on (APEX 42a2ded5 follow-up:
    // "duplicating a product with an add-on clobbers the gift" — really
    // the gift never persisted in the first place). Fall back to a global
    // lookup by key if a caller ever omits the cell.
    if (!cell && itemKey) {
      cell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + itemKey + '"]');
    }
    if (!cell) return;

    var checkbox = cell.querySelector('.gldn-gift-checkbox');
    var isGift = checkbox.checked;
    var giftValue = isGift ? 'Yes' : '';
    var noteValue = '';

    if (isGift) {
      /* #593 — cleaned again here, not only on input: a value filled in
         without an input event (an emoji note saved before the filter
         existed, hydrated from /cart.js) must not be written back. */
      var to = cleanValue(cell.querySelector('.gldn-gift-to'));
      var from = cleanValue(cell.querySelector('.gldn-gift-from'));
      var note = cleanValue(cell.querySelector('.gldn-gift-textarea'));
      noteValue = joinGiftNote(to, from, note);
    }

    var saveBtn = cell.querySelector('.gldn-gift-save');
    if (saveBtn) saveBtn.disabled = true;
    clearSaveError(cell);

    // Snapshot all line-item keys sharing this variantId BEFORE the change.
    // After the save, the new key is whichever one isn't in this snapshot.
    // Variant-id-only matching breaks when Duplicate has produced a second
    // line with the same variant (APEX 42a2ded5).
    var variantId = itemKey.split(':')[0];
    var prevKeysForVariant = [];

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        prevKeysForVariant = (cart.items || [])
          .filter(function (i) { return i.key.split(':')[0] === variantId; })
          .map(function (i) { return i.key; });

        var item = (cart.items || []).find(function (i) { return i.key === itemKey; });
        // Stale-key self-heal: after a FIRST save (or a gift-box write) the line
        // key rotates, but this handler's `itemKey` was captured once at
        // createGiftUI time and is never reassigned. A second save (e.g. "Edit
        // Gift Details" → change recipient → Save) would post to a dead key —
        // Shopify no-ops it and the edit is silently lost on reload. Re-resolve
        // by the stable _unique / _random_id (data-gldn-unique on the row) and
        // write to the LIVE key instead. (gldn45 #334 QA — read-only-edit repro.)
        if (!item && row) {
          var uniq = row.getAttribute('data-gldn-unique');
          if (uniq) {
            item = (cart.items || []).find(function (i) {
              var p = i.properties || {};
              return (p._unique || p._random_id) === uniq;
            });
          }
          if (item) itemKey = item.key;
        }
        if (!item) {
          // The line genuinely isn't in the cart anymore — surface it (the
          // .catch below shows the save error + re-enables the button) rather
          // than posting to a dead key (a silent no-op).
          throw new Error('gift line no longer in cart: ' + itemKey);
        }
        var existingProps = item && item.properties ? Object.assign({}, item.properties) : {};
        var finalNote = noteValue;
        // Drop the two keys we're about to overwrite
        delete existingProps.Gift;
        delete existingProps['Gift Note'];

        var properties = Object.assign({}, existingProps, {
          Gift: giftValue,
          'Gift Note': finalNote
        });

        // ALWAYS send the line's current quantity. /cart/change.js treats a
        // missing `quantity` as 1 — a property-only write silently collapses a
        // qty-3 line to qty 1 (gldn99 #416; verified against the store: qty 3
        // → 1 without this key, 3 → 3 with it). `item` here is the canonical
        // line read from /cart.js above, so this is the server's own count.
        return postCartChange({
          id: itemKey,
          quantity: item.quantity,
          properties: properties
        });
      })
      .then(function (cart) {
        clearSaveError(cell);
        var newKey = findNewKey(cart, itemKey, prevKeysForVariant);
        if (newKey && newKey !== itemKey) {
          cell.setAttribute('data-gldn-for-key', newKey);
          row.setAttribute('data-key', newKey);
        }
      })
      .catch(function (err) {
        console.error('Gift note save failed:', err);
        showSaveError(cell);
      })
      .finally(function () {
        if (saveBtn) saveBtn.disabled = false;
      });
  }

  /** After property change, the item key changes. Find the new one.
   *
   * `prevKeysForVariant` is the list of line-item keys sharing this
   * variantId BEFORE the change. The new key is whichever variant-matching
   * key isn't in that snapshot. Falls back to first-variant-match for
   * call sites that don't pass a snapshot (kept stable so existing callers
   * still work in the single-line-per-variant case). */
  function findNewKey(cart, oldKey, prevKeysForVariant) {
    if (!cart || !cart.items) return oldKey;
    var oldVariantId = oldKey.split(':')[0];
    var variantMatches = cart.items
      .map(function (i) { return i.key; })
      .filter(function (k) { return k.split(':')[0] === oldVariantId; });
    if (variantMatches.length === 0) return oldKey;
    if (Array.isArray(prevKeysForVariant) && prevKeysForVariant.length) {
      var fresh = variantMatches.find(function (k) {
        return prevKeysForVariant.indexOf(k) === -1;
      });
      if (fresh) return fresh;
    }
    return variantMatches[0];
  }

  /** Collect form state from all gift cells before a morph destroys them.
   *
   * Keyed by the full line-item key, NOT variantId. Two distinct cart
   * lines can share a variantId (Duplicate creates one) and keying by
   * variantId collapses them into one entry — the second cell's state
   * overwrites the first, then restoreFormState writes the merged state
   * into BOTH cells, producing the "typing on original applies to
   * duplicate" + "duplicate not typeable" bugs from APEX 42a2ded5. */
  function collectFormState() {
    var state = {};
    document.querySelectorAll('.gldn-gift-cell').forEach(function (cell) {
      var forKey = cell.getAttribute('data-gldn-for-key');
      if (!forKey) return;
      var f = cell.querySelector('.gldn-gift-form');
      state[forKey] = {
        checked: cell.querySelector('.gldn-gift-checkbox')?.checked || false,
        to: cell.querySelector('.gldn-gift-to')?.value || '',
        from: cell.querySelector('.gldn-gift-from')?.value || '',
        note: cell.querySelector('.gldn-gift-textarea')?.value || '',
        formOpen: f?.classList.contains('open') || false,
        // Editing = form open but not collapsed-to-saved (To/From editable).
        editing: !!(f && f.classList.contains('open') && !f.classList.contains('saved'))
      };
    });
    return state;
  }

  /** Restore form state after re-injection. Keyed by full line-item key
   *  to match collectFormState (see comment above). */
  function restoreFormState(state) {
    document.querySelectorAll('.gldn-gift-cell').forEach(function (cell) {
      var forKey = cell.getAttribute('data-gldn-for-key');
      if (!forKey) return;
      var saved = state[forKey];
      if (!saved) return;

      var checkbox = cell.querySelector('.gldn-gift-checkbox');
      var editBtn = cell.querySelector('.gldn-gift-edit');
      var form = cell.querySelector('.gldn-gift-form');
      var toInput = cell.querySelector('.gldn-gift-to');
      var fromInput = cell.querySelector('.gldn-gift-from');
      var textarea = cell.querySelector('.gldn-gift-textarea');
      var counter = cell.querySelector('.gldn-gift-count');

      // Change-only writes. Unconditionally re-setting counter.textContent (a
      // childList mutation) on every re-inject re-triggered the observeCartUpdates
      // MutationObserver, which re-scheduled another re-inject — a self-sustaining
      // reflow loop once any external mutation (e.g. gldn45 #334 gift-box row
      // grouping) kicked off the first re-inject. Only writing on an actual
      // change breaks the loop (gldn45 #334 review r3).
      if (checkbox && checkbox.checked !== saved.checked) checkbox.checked = saved.checked;
      if (toInput && toInput.value !== saved.to) toInput.value = saved.to;
      if (fromInput && fromInput.value !== saved.from) fromInput.value = saved.from;
      if (textarea) {
        if (textarea.value !== saved.note) textarea.value = saved.note;
        var _countStr = String(saved.note.length);
        if (counter && counter.textContent !== _countStr) counter.textContent = _countStr;
      }
      if (saved.checked && editBtn && editBtn.style.display !== '') editBtn.style.display = '';
      if (saved.formOpen && form && !form.classList.contains('open')) form.classList.add('open');
      // Restore the editing-vs-saved distinction so read-only survives a morph.
      if (form) {
        if (saved.editing) form.classList.remove('saved');
        // #599 — an open, non-editing form IS the saved view (see
        // collectFormState), and a fresh cell may not carry the class.
        else if (saved.formOpen && !form.classList.contains('saved')) form.classList.add('saved');
        var ro = !saved.editing;
        if (toInput && toInput.readOnly !== ro) toInput.readOnly = ro;
        if (fromInput && fromInput.readOnly !== ro) fromInput.readOnly = ro;
      }
      renderGiftSummary(cell);
    });
  }

  /** Hydrate every gift cell from the canonical /cart.js properties.
   *
   * The cart template filters out Gift / Gift Note from the visible
   * <dt>/<dd> property list (those are surfaced via the gift cell UI
   * instead). That means readGiftProps — which reads from the DOM —
   * never sees them on a fresh row, so freshly-injected cells for a
   * line that already has Gift=Yes saved server-side render unchecked.
   * This bit users hard after Duplicate: properties were copied to the
   * new line correctly, but the duplicated cell's checkbox + fields
   * showed empty (APEX 42a2ded5 follow-up).
   *
   * Fetch the canonical cart, find each cell's matching line, and
   * fill the checkbox + fields in place — but only if the cell isn't
   * already in user-input state (don't clobber an open form the user
   * is currently typing into).
   *
   * Coalesces concurrent requests via a single in-flight promise +
   * a short post-resolve cooldown — so the morph observer / reinject
   * loop can call this freely without exhausting the browser's
   * parallel-fetch budget on /cart.js. */
  var _hydrateInFlight = null;
  var _hydrateLastAt = 0;
  var _hydratePendingTimer = null;
  function hydrateGiftCellsFromCart() {
    if (_hydrateInFlight) return _hydrateInFlight;
    // Short cooldown — at most one /cart.js fetch per 750ms. If a request
    // arrives during cooldown, schedule a single deferred run for when the
    // cooldown expires so we don't drop "fire-after-duplicate" hydrate
    // requests that arrive right after init's first run.
    var sinceLast = Date.now() - _hydrateLastAt;
    if (sinceLast < 750) {
      if (!_hydratePendingTimer) {
        _hydratePendingTimer = setTimeout(function () {
          _hydratePendingTimer = null;
          hydrateGiftCellsFromCart();
        }, 750 - sinceLast + 50);
      }
      return null;
    }

    // gldn95 (#405): hydrate is a page-load read of the same body every other
    // cart script wants, so take the shared coalesced snapshot when available.
    // Writes invalidate it, so this can't hydrate cells from a pre-write cart.
    _hydrateInFlight = (typeof window.gldnCartSnapshot === 'function'
      ? window.gldnCartSnapshot()
      : fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); })
    )
      .then(function (cart) {
        var byKey = {};
        (cart.items || []).forEach(function (i) { byKey[i.key] = i.properties || {}; });

        document.querySelectorAll('.gldn-gift-cell').forEach(function (cell) {
          var forKey = cell.getAttribute('data-gldn-for-key');
          var props = byKey[forKey];
          if (!props) return;
          if (props.Gift !== 'Yes') return;

          var checkbox = cell.querySelector('.gldn-gift-checkbox');
          if (!checkbox || checkbox.checked) return; // already shown / already in user-input state

          var parsed = parseGiftNote(props['Gift Note'] || '');
          var toInput = cell.querySelector('.gldn-gift-to');
          var fromInput = cell.querySelector('.gldn-gift-from');
          var textarea = cell.querySelector('.gldn-gift-textarea');
          var counter = cell.querySelector('.gldn-gift-count');
          var form = cell.querySelector('.gldn-gift-form');
          var editBtn = cell.querySelector('.gldn-gift-edit');

          checkbox.checked = true;
          if (toInput) toInput.value = parsed.to;
          if (fromInput) fromInput.value = parsed.from;
          if (textarea) {
            textarea.value = parsed.note;
            if (counter) counter.textContent = parsed.note.length;
          }
          if (form) form.classList.add('open', 'saved');
          if (editBtn) editBtn.style.display = '';
          // Hydrated cells land in the collapsed "saved" state → read-only.
          if (toInput) toInput.readOnly = true;
          if (fromInput) fromInput.readOnly = true;
          renderGiftSummary(cell);
        });
      })
      .catch(function () { /* leave cells as-is on network failure */ })
      .finally(function () {
        _hydrateInFlight = null;
        _hydrateLastAt = Date.now();
      });
    return _hydrateInFlight;
  }

  /** Inject gift UI into all cart item rows */
  function injectAll() {
    var rows = document.querySelectorAll('.cart-items__table-row[data-key]');
    rows.forEach(createGiftUI);
  }

  /** Gift cells whose checkbox is ticked. Only these are validated. */
  function checkedGiftCells(cells) {
    var out = [];
    (cells || document.querySelectorAll('.gldn-gift-cell')).forEach(function (cell) {
      if (cell.querySelector('.gldn-gift-checkbox')?.checked) out.push(cell);
    });
    return out;
  }

  /** Validate To AND From on every ticked cell. On failure, reopen each
   *  offending editor, mark its fields, scroll to the first and toast the
   *  item names; returns false. Empty Gift Note is allowed (it is optional). */
  function validateGiftCells(giftCells) {
    var invalidItems = [];
    giftCells.forEach(function (cell) {
      var toInput = cell.querySelector('.gldn-gift-to');
      var fromInput = cell.querySelector('.gldn-gift-from');
      var toEmpty = !cleanValue(toInput);
      var fromEmpty = !cleanValue(fromInput);
      if (toEmpty) showFieldError(cell, '.gldn-gift-to', 'Recipient name is required.');
      else clearFieldError(cell, '.gldn-gift-to');
      if (fromEmpty) showFieldError(cell, '.gldn-gift-from', 'Sender name is required.');
      else clearFieldError(cell, '.gldn-gift-from');
      if (toEmpty || fromEmpty) {
        var row = cell.closest('.cart-items__table-row');
        var title = row ? (row.querySelector('.cart-items__title')?.innerText?.trim() || 'this item') : 'this item';
        invalidItems.push({ title: title, cell: cell, missing: { to: toEmpty, from: fromEmpty } });
      }
    });
    if (invalidItems.length === 0) return true;

    /* #599 — the saved view hides the fields, so every offending form
       goes back to the editable editor or its highlighted field would
       not be on screen. */
    invalidItems.forEach(function (it) {
      var f = it.cell.querySelector('.gldn-gift-form');
      if (f) { f.classList.add('open'); f.classList.remove('saved'); }
      it.cell.querySelectorAll('.gldn-gift-to, .gldn-gift-from').forEach(function (input) {
        input.readOnly = false;
      });
    });

    /* Scroll the first offending gift form's highlighted field into view. */
    var first = invalidItems[0];
    var firstField = first.cell.querySelector('.gldn-gift-to.gldn-gift-field-invalid')
                  || first.cell.querySelector('.gldn-gift-from.gldn-gift-field-invalid');
    if (firstField) {
      firstField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstField.focus({ preventScroll: true });
    }

    /* Toast naming all items missing details. */
    var msg;
    if (invalidItems.length === 1) {
      msg = 'Please complete the gift To/From fields for ' + invalidItems[0].title + ' before checkout.';
    } else {
      var titles = invalidItems.map(function (it) { return it.title; }).join(', ');
      msg = 'Please complete the gift To/From fields for: ' + titles;
    }
    showToast(msg);
    return false;
  }

  /** Write every cell whose gift state differs from the cart. Resolves once
   *  all writes settle and never rejects.
   *
   *  Fetch the canonical cart state, then merge gift properties into each
   *  affected line item before POSTing to /cart/change.js.
   *
   *  The previous implementation sent only { Gift, "Gift Note" }, which —
   *  because /cart/change.js REPLACES the line-item properties hash —
   *  silently wiped every other property on every cart line (My Garden
   *  flower personalization, personalizer Builder/Preview/Customization
   *  metadata, the _unique de-dup key, etc.). It also iterated over ALL
   *  pendingCells, so a single gift-wrapped item would clobber
   *  personalization on every other (non-gift-wrapped) line too — the
   *  no-op write in saveGiftDetailsAsync now prevents that.
   *
   *  Promise.allSettled (not Promise.all) keeps a single failed save from
   *  stranding the user on the cart page. Reaching checkout is
   *  funnel-critical; a missing gift-note write is recoverable (user can
   *  re-enter it later) but a stuck-on-cart dead-click is not. If the
   *  /cart.js prefetch itself fails, skip the writes entirely rather than
   *  risk a wipe — the caller still proceeds. */
  function saveAllGiftCells(pendingCells) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) return [];
        var promises = [];
        pendingCells.forEach(function (cell) {
          var forKey = cell.getAttribute('data-gldn-for-key');
          if (!forKey) return;
          var p = saveGiftDetailsAsync(cell, forKey, cart);
          if (p) promises.push(p);
        });
        return Promise.allSettled(promises);
      })
      .then(function (results) {
        if (Array.isArray(results)) {
          var failures = results.filter(function (r) { return r.status === 'rejected'; });
          if (failures.length) {
            console.warn('gift-note save failures (proceeding to checkout anyway):', failures);
          }
        }
      })
      .catch(function (err) {
        console.warn('gift-note prefetch failed (proceeding to checkout anyway):', err);
      });
  }

  /** Intercept checkout to validate + save pending gift details */
  function interceptCheckout() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.cart__checkout-button, #checkout, [name="checkout"]');
      if (!btn) return;

      var pendingCells = document.querySelectorAll('.gldn-gift-cell');
      if (pendingCells.length === 0) return;

      var giftCells = checkedGiftCells(pendingCells);
      if (giftCells.length === 0) return;

      if (!validateGiftCells(giftCells)) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      saveAllGiftCells(pendingCells).then(function () {
        window.location.href = '/checkout';
      });
    }, true);
  }

  /* #599 — the same guard for Redo's Checkout+ / "Checkout without…" buttons.
   * A tap anywhere in Redo's widget reaches these document capture listeners
   * as an event on the <redo-shopify-toggle> host, before Redo's own handlers
   * inside the closed root, so it can be held here: validate, save, then
   * click the very control the shopper tapped so Redo continues with their
   * coverage choice. Redo starts checkout on POINTERUP (measured: holding
   * pointerup stops it, holding pointerdown or mousedown does not), and also
   * on click, which keyboard activation sends alone; both are held, and the
   * click that trails a held tap is swallowed so one tap runs the flow once. Without the captured root (a Redo widget attached before
   * this script) the control is unknown, so the tap is held only while there
   * is a ticked gift, and after saving the shopper is asked to tap again; a
   * short pass lets that second tap through unless the gift is edited. */
  var REDO_PASS_MS = 15000;

  function giftSignature() {
    var parts = [];
    document.querySelectorAll('.gldn-gift-cell').forEach(function (cell) {
      parts.push([
        cell.getAttribute('data-gldn-for-key'),
        cell.querySelector('.gldn-gift-checkbox')?.checked ? 1 : 0,
        cleanValue(cell.querySelector('.gldn-gift-to')),
        cleanValue(cell.querySelector('.gldn-gift-from')),
        cleanValue(cell.querySelector('.gldn-gift-textarea'))
      ].join('\u0000'));
    });
    return parts.join('\u0001');
  }

  /** Which of Redo's controls a click landed on, as 'plus' / 'without', or
   *  null for one that is not a checkout (the coverage toggle, the info
   *  icon). undefined when Redo's root was not captured. */
  function redoControlKind(host, e) {
    var root = redoRoots.get(host) || host.shadowRoot;
    if (!root) return undefined;
    // A keyboard-activated click has no pointer position: use focus instead.
    var keyboard = e.type === 'click' && e.detail === 0;
    var el = keyboard
      ? root.activeElement
      : (root.elementFromPoint ? root.elementFromPoint(e.clientX, e.clientY) : null);
    var ctl = el && el.closest ? el.closest('button, a, [role="button"]') : null;
    var text = ctl ? (ctl.textContent || '') : '';
    if (!/checkout/i.test(text)) return null;
    return /without/i.test(text) ? 'without' : 'plus';
  }

  /** The live control of that kind; Redo re-renders on cart changes, so the
   *  node tapped may have been replaced while the gift details saved. */
  function findRedoControl(kind) {
    var host = document.querySelector('redo-shopify-toggle');
    var root = host && (redoRoots.get(host) || host.shadowRoot);
    if (!root) return null;
    var controls = root.querySelectorAll('button, a, [role="button"]');
    for (var i = 0; i < controls.length; i++) {
      var text = controls[i].textContent || '';
      if (!/checkout/i.test(text)) continue;
      if ((kind === 'without') === /without/i.test(text)) return controls[i];
    }
    return null;
  }

  function hold(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
  }

  function interceptRedoCheckout() {
    if (redo.listening) return;
    redo.listening = true;
    document.addEventListener('pointerup', onRedoActivate, true);
    document.addEventListener('click', onRedoActivate, true);
  }

  function onRedoActivate(e) {
      if (redo.resuming) return;
      var host = e.target && e.target.closest ? e.target.closest('redo-shopify-toggle') : null;
      if (!host) return;
      if (e.type === 'pointerup' && e.button !== 0) return;
      if (e.type === 'click' && Date.now() < redo.swallowClickUntil) {
        redo.swallowClickUntil = 0;
        hold(e); // the click that trails a tap already being handled
        return;
      }
      // A save is already running for an earlier tap: one tap, one checkout.
      if (redo.inFlight) {
        hold(e);
        return;
      }

      var pendingCells = document.querySelectorAll('.gldn-gift-cell');
      if (pendingCells.length === 0) return;
      var giftCells = checkedGiftCells(pendingCells);
      if (giftCells.length === 0) return;

      var kind = redoControlKind(host, e);
      if (kind === null) return; // the coverage toggle, not a checkout
      if (kind === undefined && redo.pass && Date.now() < redo.pass.until && redo.pass.sig === giftSignature()) {
        redo.pass = null;
        return;
      }

      hold(e);
      if (e.type === 'pointerup') redo.swallowClickUntil = Date.now() + 1000;
      if (!validateGiftCells(giftCells)) return;

      redo.inFlight = true;
      saveAllGiftCells(pendingCells).then(function () {
        redo.inFlight = false;
        var control = kind ? findRedoControl(kind) : null;
        if (control) {
          redo.resuming = true;
          try { control.click(); } finally { redo.resuming = false; }
          return;
        }
        redo.pass = { until: Date.now() + REDO_PASS_MS, sig: giftSignature() };
        showToast('Your gift details are saved. Tap checkout again to continue.');
      });
  }

  /** Lightweight in-page toast. One instance at a time — re-uses the
   *  existing node if it's still onscreen. Auto-dismisses after 6s. */
  function showToast(message) {
    var existing = document.querySelector('.gldn-gift-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.className = 'gldn-gift-toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.textContent = message;
    document.body.appendChild(toast);
    /* Trigger CSS transition by adding the visible class on next frame. */
    requestAnimationFrame(function () {
      toast.classList.add('gldn-gift-toast--visible');
    });
    setTimeout(function () {
      toast.classList.remove('gldn-gift-toast--visible');
      setTimeout(function () { if (toast.parentNode) toast.remove(); }, 300);
    }, 6000);
  }

  /** Async version of saveGiftDetails for checkout interception.
   *
   * Takes a `cart` snapshot from /cart.js so we can MERGE existing line-
   * item properties before writing. /cart/change.js replaces the whole
   * properties hash — sending only { Gift, "Gift Note" } would wipe
   * personalization (Builder/Preview/Customization), required-request
   * answers (e.g. "*required* Let us know which flowers..."), and the
   * _unique de-dup key. The sync `saveGiftDetails` has done this merge
   * for a while; the async path was missing it (live bug: order #G724590
   * shipped without the My Garden flower property on May 19 2026).
   *
   * Returns null when there is nothing to write — the line is already in
   * the desired state. Callers skip non-gift cells entirely instead of
   * stamping `Gift: ""` onto items the user never gift-wrapped (which
   * would replace the properties hash and wipe their personalization).
   */
  function saveGiftDetailsAsync(cell, itemKey, cart) {
    var checkbox = cell.querySelector('.gldn-gift-checkbox');
    var isGift = checkbox && checkbox.checked;

    var item = (cart && cart.items || []).find(function (i) { return i.key === itemKey; });
    if (!item) return null; // line item no longer exists, nothing to write
    var existingProps = Object.assign({}, item.properties || {});
    var currentGift = existingProps.Gift || '';
    var currentNote = existingProps['Gift Note'] || '';

    var giftValue = isGift ? 'Yes' : '';
    var noteValue = '';
    if (isGift) {
      var to = cleanValue(cell.querySelector('.gldn-gift-to'));
      var from = cleanValue(cell.querySelector('.gldn-gift-from'));
      var note = cleanValue(cell.querySelector('.gldn-gift-textarea'));
      noteValue = joinGiftNote(to, from, note);
    }

    if (currentGift === giftValue && currentNote === noteValue) {
      return null;
    }

    delete existingProps.Gift;
    delete existingProps['Gift Note'];
    var properties = Object.assign({}, existingProps, {
      Gift: giftValue,
      'Gift Note': noteValue
    });

    // `quantity` is mandatory on a property write: /cart/change.js defaults a
    // missing quantity to 1, so this checkout-time save used to silently reset
    // every gift-wrapped line to a single unit — the shopper set qty 2, saved a
    // gift message, and the checkout summary showed 1 (gldn99 #416). `item` is
    // the canonical line from the /cart.js snapshot the caller passed in.
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity: item.quantity, properties: properties })
    }).then(function (res) { return res.json(); });
  }

  function escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /** Watch for Horizon's morphSection updates and re-inject.
   *
   * Three layers of observation cover the morph patterns Horizon uses:
   *   1. tbody subtree (childList + subtree) — catches both row replacements
   *      AND in-row mutations where morph removes our injected gldn-gift-cell
   *      <td> while keeping the row itself. subtree:false would miss the
   *      latter (the row's children changed, not the tbody's).
   *   2. cart-items-component childList — catches when the entire tbody
   *      element gets swapped wholesale (morph in hydration mode sometimes
   *      replaces the whole cart-items-component contents).
   *   3. The body MutationObserver further down handles table appearance.
   *
   * Each observer feeds back into injectAll, which is idempotent (createGiftUI
   * bails on rows that already have a cell), so the multiple observers won't
   * cause duplicate cells. rAF debounces multiple mutations into one pass.
   */
  /* gldn167 (#554): these observers are never disconnected, and they used to be
   * tracked ONLY by a flag on the node (`tbody._gldnGiftObserver`). A morph that
   * REPLACES the tbody or the component gives the fresh node no flag, so a new
   * observer is attached while the old one stays live on a detached subtree —
   * one leaked observer per morph, each retaining the DOM it was watching.
   * observeCartUpdates() is also called from five places (init, scheduleReinject,
   * the retry timers), so the churn is per cart mutation, not per page.
   *
   * Keep a registry and prune anything no longer in the document before
   * attaching. That bounds the live observer count to the number of live nodes
   * and keeps the node flag's "don't double-observe a surviving node" behaviour. */
  var _giftObservers = [];

  function observeCartUpdates() {
    for (var i = _giftObservers.length - 1; i >= 0; i--) {
      if (!_giftObservers[i].target.isConnected) {
        _giftObservers[i].observer.disconnect();
        /* Clear the node flag too. A morph can detach a node and put it BACK;
         * if the flag survived a prune the node would be skipped below and end
         * up permanently unobserved, which stops gift cells being re-injected
         * after the next morph. Dropping the flag lets it be re-observed. */
        delete _giftObservers[i].target._gldnGiftObserver;
        _giftObservers.splice(i, 1);
      }
    }

    var targets = Array.prototype.slice
      .call(document.querySelectorAll('.cart-items__table tbody'))
      .concat(Array.prototype.slice.call(document.querySelectorAll('cart-items-component')));

    targets.forEach(function (node) {
      if (node._gldnGiftObserver) return;
      var observer = new MutationObserver(scheduleReinject);
      observer.observe(node, { childList: true, subtree: true });
      node._gldnGiftObserver = true;
      _giftObservers.push({ observer: observer, target: node });
    });
  }

  /* Debounced re-inject. Multiple mutations from a single morph batch into
   * one injectAll call on the next animation frame. Also prevents re-entry
   * during the inject itself (so our own DOM writes don't loop the observer). */
  var _reinjectScheduled = false;
  var _reinjectInProgress = false;
  function scheduleReinject() {
    if (_reinjectInProgress) return;
    if (_reinjectScheduled) return;
    _reinjectScheduled = true;
    requestAnimationFrame(function () {
      _reinjectScheduled = false;
      _reinjectInProgress = true;
      try {
        var state = collectFormState();
        injectAll();
        observeCartUpdates();
        restoreFormState(state);
        hydrateGiftCellsFromCart();
      } finally {
        _reinjectInProgress = false;
      }
    });
  }

  /** Initialize per-render concerns (gift-cell injection + cart-update observer).
   *
   * NOTE: `interceptCheckout()` is registered ONCE at the IIFE-level setup
   * below, NOT here. Previously it was called from `init()` which runs on
   * every cart-render — and `init()` is wired up via 4 retry timers
   * (initWithRetries), a `dialog:open` listener (with 3 more retries), a
   * cart-drawer-trigger click handler, and a body-level MutationObserver.
   * A normal cart drawer open/close cycle accumulated 8+ duplicate
   * document-level capture-phase listeners, each registered as a fresh
   * anonymous function (so they don't deduplicate). On the gift-checkbox-
   * checked path every duplicate fired its own `Promise.all` save chain
   * and its own `window.location.href = '/checkout'`, racing the others.
   * Lifting the registration out of init() makes it one-shot. */
  function init() {
    var tbodies = document.querySelectorAll('.cart-items__table tbody');
    if (!tbodies.length) return;

    injectAll();
    observeCartUpdates();
    hydrateGiftCellsFromCart();
  }

  /** Register the checkout-button click interceptor exactly once per page
   * load (see the comment in init() above for the multiple-listener bug
   * this fix addresses). Safe to call before any cart is rendered — the
   * handler checks `pendingCells.length` at click time, not at register
   * time, so it no-ops on pages without gift cells. */
  interceptCheckout();
  interceptRedoCheckout();

  // Expose so external scripts can re-run after swapping cart section HTML.
  // (Needed when the personalizer's addToCartSuccess replaces the cart-drawer
  // section innerHTML — the old tbody gets destroyed with its observer,
  // and the fresh tbody needs new injection + observer.)
  window.gldnGiftNoteInit = init;
  // Cheaper re-inject used from rAF / retry loops — just (re)walks rows
  // and creates gift cells. Safe to call repeatedly (createGiftUI bails
  // if the cell already exists).
  window.gldnGiftNoteRefresh = function () {
    try { injectAll(); observeCartUpdates(); } catch (e) { /* noop */ }
  };

  // Run when DOM is ready, then retry several times to cover edge cases
  // where init runs before Horizon's cart-items-component has finished
  // upgrading + connecting (a common race on fresh page loads where the
  // tbody exists in markup but the component lifecycle hasn't started
  // yet, so a downstream morph wipes our injected cells). Each retry is
  // idempotent — createGiftUI bails on rows that already have a cell.
  function initWithRetries() {
    init();
    setTimeout(init, 50);
    setTimeout(init, 250);
    setTimeout(init, 750);
    setTimeout(init, 1500);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWithRetries);
  } else {
    initWithRetries();
  }
  // Also retry once the page finishes loading (resources + late-loading
  // modules like the cart-items-component custom element registration).
  window.addEventListener('load', function () {
    init();
    setTimeout(init, 100);
  });

  // Horizon's cart-items-component re-renders the cart section on every
  // cart:update event (morphSection wipes any JS-injected cells we had).
  // Re-hydrate the gift form after Horizon finishes — multiple scheduled
  // passes cover both the sync morph (when event.detail.data.sections is
  // set) and the async re-fetch morph (cache:false renderSection).
  // 1000ms backstop catches slow morphs on the very first cart add
  // from an empty cart (incognito / fresh session) where the drawer's
  // entire inner content swaps from empty-state to the full cart table.
  document.addEventListener('cart:update', function () {
    requestAnimationFrame(init);
    setTimeout(init, 150);
    setTimeout(init, 500);
    setTimeout(init, 1000);
    /* #418 — the summary grows and shrinks with the cart (discounts, shipping
     * line, free-gift rows), so re-measure whenever it changes. */
    requestAnimationFrame(syncCartSummaryHeight);
    setTimeout(syncCartSummaryHeight, 500);
  });

  // Also re-inject when cart drawer dialog opens
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-testid="cart-drawer-trigger"]')) {
      setTimeout(function () { injectAll(); observeCartUpdates(); }, 100);
    }
  });

  // Cart drawer auto-opens after add-to-cart via DialogOpenEvent — listen
  // for that too so we cover the first-add-from-empty-cart case where the
  // user never clicks the cart-drawer-trigger.
  document.addEventListener('dialog:open', function (e) {
    var t = e.target;
    if (!t || !(t.closest && t.closest('cart-drawer-component'))) return;
    requestAnimationFrame(init);
    setTimeout(init, 100);
    setTimeout(init, 300);
    setTimeout(syncCartSummaryHeight, 300); // #418
  });

  // Final safety net: MutationObserver on document.body that triggers init
  // the moment a cart-items table appears. Catches any morph timing edge
  // case the timed retries miss.
  if (typeof MutationObserver !== 'undefined') {
    var seenTable = !!document.querySelector('.cart-items__table');
    var bodyObserver = new MutationObserver(function () {
      var hasTable = !!document.querySelector('.cart-items__table');
      if (hasTable && !seenTable) {
        seenTable = true;
        requestAnimationFrame(init);
      } else if (!hasTable && seenTable) {
        // Cart was emptied — reset the flag so the next non-empty render
        // re-triggers init.
        seenTable = false;
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
